import { prettyDateTime } from "@app/date";
import {
  fetchExternalAwsResources,
  selectExternalAwsAccountById,
  selectExternalAwsResourcesByAccountId,
} from "@app/deploy";
import { useDispatch, useQuery, useSelector } from "@app/react";
import { externalAwsResourceConnectionsUrl } from "@app/routes";
import { useExternalAwsAccountSyncOpsPoller, useInterval } from "@app/ui/hooks";
import { useCallback, useEffect } from "react";
import { Link, useParams } from "react-router";
import {
  DescBar,
  DetailPageSections,
  EmptyTr,
  FilterBar,
  Group,
  LoadingBar,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  tokens,
} from "../shared";

function prettyExternalAwsResourceType(resourceType: string): string {
  switch (resourceType) {
    case "aws_rds_db_instance":
      return "AWS RDS Database";
    default:
      return "Unknown";
  }
}

export const SettingsAccountIntegrationsAwsDetailPage = () => {
  const dispatch = useDispatch();
  const { id = "" } = useParams();
  const account = useSelector((s) => selectExternalAwsAccountById(s, { id }));
  const { hasActiveSync, isTabActive } = useExternalAwsAccountSyncOpsPoller([
    account.id,
  ]);
  const { isLoading } = useQuery(fetchExternalAwsResources());
  const resources = useSelector((s) =>
    selectExternalAwsResourcesByAccountId(s, { accountId: account.id }),
  );
  const refreshResources = useCallback(() => {
    dispatch(fetchExternalAwsResources());
  }, [dispatch]);

  useEffect(() => {
    if (!isTabActive) {
      return;
    }
    refreshResources();
  }, [isTabActive, refreshResources]);

  useInterval(
    () => {
      if (!isTabActive) {
        return;
      }
      refreshResources();
    },
    isTabActive && account.id ? (hasActiveSync ? 1000 : 10 * 1000) : null,
  );

  return (
    <div className="py-2">
      <div className="mb-4">
        <DetailPageSections>
          <Group variant="horizontal" size="sm" className="items-center">
            <LoadingBar isLoading={isLoading} />

            <FilterBar>
              <Group
                variant="horizontal"
                size="lg"
                className="items-center my-2"
              >
                <DescBar>{resources.length || 0} Resources</DescBar>
              </Group>
            </FilterBar>
          </Group>
          <Table>
            <THead>
              <Th>Name</Th>
              <Th>ARN</Th>
              <Th>Region</Th>
              <Th>Last Synced</Th>
              <Th>Sync Status</Th>
            </THead>
            <TBody>
              {resources.length === 0 ? (
                <EmptyTr colSpan={5} />
              ) : (
                resources.map((r) => (
                  <Tr key={r.id}>
                    <Td>
                      <div className="flex">
                        <div className="flex">
                          <img
                            src={"/resource-types/logo-database.png"}
                            className="w-[32px] h-[32px] mr-2 mt-1 align-middle"
                            aria-label="AWS Resource"
                          />
                          <p className="flex flex-col">
                            <span className={tokens.type["table link"]}>
                              <Link
                                to={externalAwsResourceConnectionsUrl(r.id)}
                                className={tokens.type["table link"]}
                              >
                                {r.resourceName || r.resourceId}
                              </Link>
                            </span>
                            <span className={tokens.type["normal lighter"]}>
                              {prettyExternalAwsResourceType(r.resourceType)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td className="truncate max-w-[320px]">{r.resourceArn}</Td>
                    <Td>{r.region}</Td>
                    <Td>
                      {r.lastSyncedAt ? (
                        prettyDateTime(r.lastSyncedAt)
                      ) : (
                        <em>Not yet synced</em>
                      )}
                    </Td>
                    <Td>
                      {r.syncStatus.charAt(0).toUpperCase() +
                        r.syncStatus.slice(1)}
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </DetailPageSections>
      </div>
    </div>
  );
};
